import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import { Button, Loader, Segment } from 'semantic-ui-react';
import { Notification, STATUS_TYPES, VALIDATORS, generateResolver, yup } from 'dyl-components';

import onboardingActions from 'actions/onboarding';
import rolesActions from 'actions/roles';
import { StringUtils } from 'utils';
import { useConfirm } from 'shared/confirmation/useConfirm';
import TeamStructure from './TeamStructure';
import InviteTeam from './InviteTeam';
import ConfirmTeamSetup from './ConfirmTeamSetup';
import { TEAM_SETUP_SCREENS } from '../constants';

// Business profile answers the team suggestions are based on
const TEAM_INPUT_FIELDS = ['industry', 'team_size', 'primary_goal'];
const pickTeamInputs = (profile = {}) => TEAM_INPUT_FIELDS.reduce((inputs, field) => ({ ...inputs, [field]: profile[field] || '' }), {});

// Used only if the account's roles can't be loaded
const FALLBACK_PREDEFINED_ROLES = [{ id: 'owner', name: 'owner', editable: false }, { id: 'user', name: 'user', editable: false }];

// DYL's predefined access roles are the non-editable ones (shown as "(Default)" in Settings → Permissions)
const toPredefinedRoles = (accountRoles = []) => {
    const predefined = accountRoles.filter(({ editable }) => editable === false);
    return (predefined.length ? predefined : FALLBACK_PREDEFINED_ROLES).map(({ id, name }) => ({
        id,
        name: StringUtils.capitalize(name),
        summary: 'Standard DYL role',
        permissions: [],
        locked: name.toLowerCase() === 'owner',
        selected: true,
        predefined: true
    }));
}

const getTeamNameError = (name, otherNames) => {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
        return 'Team name must be at least 2 characters';
    }
    if (trimmed.length > 64) {
        return 'Team name must be 64 characters or less';
    }
    if (otherNames.includes(trimmed.toLowerCase())) {
        return 'Team names must be unique';
    }
    return null;
}

const TeamSetupStep = ({ businessProfile, draft, onDraftChange, onBack, onComplete, nextStepTitle }) => {
    const dispatch = useDispatch();
    const { isConfirmed } = useConfirm();

    const { user_id, hasCustomRoles, teamLimit, seatInfo, accountRoles, isGeneratingTeamStructure, isExtractingInvites } = useSelector(state => ({
        user_id: state.auth.user_id,
        // Suggest custom roles only when the plan lets the customer create them
        hasCustomRoles: !!state.onboarding.plan?.custom_roles,
        // null when the plan doesn't limit teams
        teamLimit: state.onboarding.plan?.team_limit ?? null,
        seatInfo: state.onboarding.seats,
        accountRoles: state.roles.roles,
        isGeneratingTeamStructure: state.onboarding.isGeneratingTeamStructure,
        isExtractingInvites: state.onboarding.isExtractingInvites
    }));

    const [screen, setScreen] = useState(draft?.approval ? TEAM_SETUP_SCREENS.CONFIRM : (draft?.screen || TEAM_SETUP_SCREENS.STRUCTURE));
    const [teams, setTeams] = useState(draft?.teams || []);
    const [roles, setRoles] = useState(draft?.roles || []);
    const [suggestionDetails, setSuggestionDetails] = useState(draft?.suggestion_details || {});
    const [generatedFor, setGeneratedFor] = useState(draft?.generated_for || null);
    const [approval, setApproval] = useState(draft?.approval || null);

    const { control, getValues, watch, formState: { isValid } } = useForm({
        mode: 'onChange',
        defaultValues: { invites: draft?.invites || [] },
        resolver: generateResolver({
            invites: yup.array().of(yup.object().shape({
                first_name: VALIDATORS.FIRST_NAME().required('This field is required'),
                last_name: VALIDATORS.LAST_NAME().required('This field is required'),
                email: VALIDATORS.EMAIL_ADDRESS().required('This field is required'),
                role_id: yup.mixed().test('required', 'Select a role', value => value !== '' && value !== null && value !== undefined),
                team_ids: yup.array().of(yup.string())
            }))
        })
    });
    const { fields: inviteFields, append, remove, replace } = useFieldArray({ control, name: 'invites' });

    const invites = watch('invites') || [];
    const invitesJSON = JSON.stringify(invites);

    useEffect(() => {
        dispatch(rolesActions.readRoles()).catch(() => {});
    }, [dispatch]);

    const predefinedRoles = useMemo(() => toPredefinedRoles(accountRoles), [accountRoles]);
    const availableRoles = hasCustomRoles ? roles : predefinedRoles;

    // Plans without teams (team_add: 0) skip them entirely
    const selectedTeams = teamLimit === 0 ? [] : teams.filter(({ selected }) => selected);
    const selectedRoles = availableRoles.filter(({ selected }) => selected);
    const roleOptions = selectedRoles.filter(({ locked }) => !locked).map(({ id, name }) => ({ key: id, value: id, text: name }));
    const teamOptions = selectedTeams.map(({ id, name }) => ({ key: id, value: id, text: name }));

    const businessInputsJSON = JSON.stringify(pickTeamInputs(businessProfile));
    const isStale = !!generatedFor && JSON.stringify(generatedFor) !== businessInputsJSON;

    const generateStructure = useCallback(async () => {
        try {
            const inputs = JSON.parse(businessInputsJSON);
            const result = await dispatch(onboardingActions.generateTeamStructure(inputs));
            // Start with no more teams selected than the plan allows
            let selectedCount = 0;
            setTeams(result.teams.map(({ id, name, purpose, default_selected }) => {
                const selected = default_selected && (teamLimit === null || selectedCount < teamLimit);
                selectedCount += selected ? 1 : 0;
                return { id, name, purpose, selected, is_custom: false };
            }));
            setRoles(result.roles.map(({ default_selected, ...role }) => ({ ...role, selected: default_selected })));
            setSuggestionDetails({ default_invite_role_id: result.default_invite_role_id });
            setGeneratedFor(inputs);
            setApproval(null);
            return true;
        } catch (e) {
            Notification.alert('Failed to generate team suggestions', STATUS_TYPES.ERROR);
            return false;
        }
    }, [dispatch, businessInputsJSON, teamLimit]);

    // Suggestions are generated automatically the first time the step opens
    const hasStructure = teams.length > 0;
    useEffect(() => {
        if (!hasStructure) {
            generateStructure();
        }
    }, [hasStructure, generateStructure]);

    // An approval only covers what the user saw; any later edit requires approving again
    const approvalSnapshot = JSON.stringify({ teams: selectedTeams, roles: selectedRoles.map(({ id }) => id), invites: JSON.parse(invitesJSON) });
    useEffect(() => {
        if (approval && approval.snapshot !== approvalSnapshot) {
            setApproval(null);
        }
    }, [approval, approvalSnapshot]);

    useEffect(() => {
        onDraftChange({
            screen,
            teams,
            roles,
            suggestion_details: suggestionDetails,
            generated_for: generatedFor,
            invites: JSON.parse(invitesJSON),
            approval
        });
    }, [onDraftChange, screen, teams, roles, suggestionDetails, generatedFor, invitesJSON, approval]);

    const onRegenerate = async () => {
        const shouldRegenerate = await isConfirmed(
            'Regenerate team suggestions?',
            'New suggestions will replace your teams and roles. Invitations you added are kept.'
        );
        if (shouldRegenerate) {
            await generateStructure();
        }
    }

    const otherTeamNames = (id) => selectedTeams.filter(team => team.id !== id).map(({ name }) => name.trim().toLowerCase());
    const teamErrors = selectedTeams.reduce((errors, { id, name }) => {
        const error = getTeamNameError(name, otherTeamNames(id));
        return error ? { ...errors, [id]: error } : errors;
    }, {});
    const areTeamsIncluded = teamLimit !== 0;
    const isOverTeamLimit = teamLimit !== null && selectedTeams.length > teamLimit;
    const canContinueToInvites = (!areTeamsIncluded || selectedTeams.length > 0) && !isOverTeamLimit && !Object.keys(teamErrors).length;

    const updateTeam = (id, changes) => setTeams(teams.map(team => team.id === id ? { ...team, ...changes } : team));

    const getDefaultRoleId = () => {
        // With predefined roles, new teammates start as a regular user
        const default_invite_role_id = hasCustomRoles
            ? suggestionDetails.default_invite_role_id
            : roleOptions.find(({ text }) => text.toLowerCase() === 'user')?.value;
        return roleOptions.some(({ value }) => value === default_invite_role_id) ? default_invite_role_id : roleOptions[0]?.value || '';
    }

    const getDefaultTeamIds = () => teamOptions.length ? [teamOptions[0].value] : [];

    const onContinueToInvites = () => {
        // Keep invites consistent with any teams or roles that were removed
        const roleIds = roleOptions.map(({ value }) => value);
        const teamIds = teamOptions.map(({ value }) => value);
        const currentInvites = getValues('invites') || [];
        if (currentInvites.length) {
            replace(currentInvites.map(invite => ({
                ...invite,
                role_id: roleIds.includes(invite.role_id) ? invite.role_id : getDefaultRoleId(),
                team_ids: (invite.team_ids || []).filter(team_id => teamIds.includes(team_id))
            })));
        }
        setScreen(TEAM_SETUP_SCREENS.INVITES);
    }

    // Each invite takes an open seat; users already on the account (including the owner) hold the used ones
    const maxInvites = seatInfo ? seatInfo.open_seats : Infinity;

    const onAddInvite = () => {
        if (invites.length >= maxInvites) {
            return;
        }
        append({ first_name: '', last_name: '', email: '', role_id: getDefaultRoleId(), team_ids: getDefaultTeamIds() });
    }

    const onPasteInvites = async (text) => {
        try {
            const { invites: extracted } = await dispatch(onboardingActions.extractInvites({ text }));
            const existingEmails = (getValues('invites') || []).map(({ email }) => email.trim().toLowerCase());
            const notYetAdded = extracted.filter(({ email }) => !existingEmails.includes(email));
            const openSeats = Math.max(0, maxInvites - existingEmails.length);
            const newInvites = notYetAdded.slice(0, openSeats);
            if (!newInvites.length) {
                Notification.alert(
                    !extracted.length ? 'No email addresses found in that list'
                        : !notYetAdded.length ? 'Everyone on that list is already added'
                            : 'Your plan has no open seats left. Add seats in Settings → Billing to invite more people.',
                    STATUS_TYPES.ERROR
                );
                return false;
            }
            append(newInvites.map(invite => ({ ...invite, role_id: getDefaultRoleId(), team_ids: getDefaultTeamIds() })));
            const duplicateCount = extracted.length - notYetAdded.length;
            const overSeatCount = notYetAdded.length - newInvites.length;
            Notification.alert(
                `Added ${newInvites.length} teammate(s)${duplicateCount ? `. Skipped ${duplicateCount} already on the list` : ''}${overSeatCount ? `. ${overSeatCount} didn't fit in your plan's seats` : ''}.`,
                STATUS_TYPES.SUCCESS
            );
            return true;
        } catch (e) {
            Notification.alert('Failed to read that list', STATUS_TYPES.ERROR);
            return false;
        }
    }

    const normalizedEmails = invites.map(({ email }) => (email || '').trim().toLowerCase());
    const duplicateEmails = normalizedEmails.reduce((duplicates, email, index) => (
        email && normalizedEmails.indexOf(email) !== index ? [...duplicates, index] : duplicates
    ), []);
    const canContinueToConfirm = (!invites.length || isValid) && !duplicateEmails.length && invites.length <= maxInvites;

    const onConfirm = () => {
        // TODO: send the approved team setup to the backend once the endpoint exists
        const newApproval = { approved_by: user_id, approved_at: new Date().toISOString(), snapshot: approvalSnapshot };
        setApproval(newApproval);
        onComplete({
            screen,
            teams,
            roles,
            suggestion_details: suggestionDetails,
            generated_for: generatedFor,
            invites: getValues('invites') || [],
            approval: newApproval
        });
        Notification.alert('Team setup saved as a draft', STATUS_TYPES.SUCCESS);
    }

    if (!hasStructure) {
        return (
            <Segment placeholder textAlign='center'>
                {isGeneratingTeamStructure ? (
                    <Loader active inline='centered'>Suggesting a team structure for your business…</Loader>
                ) : (
                    <p>We couldn't generate team suggestions. <Button basic size='small' onClick={generateStructure}>Try Again</Button></p>
                )}
            </Segment>
        );
    }

    switch (screen) {
        case TEAM_SETUP_SCREENS.INVITES:
            return (
                <InviteTeam
                    control={control}
                    invites={inviteFields}
                    roleOptions={roleOptions}
                    teamOptions={teamOptions}
                    duplicateEmails={duplicateEmails}
                    seatInfo={seatInfo}
                    maxInvites={maxInvites}
                    canContinue={canContinueToConfirm}
                    isExtractingInvites={isExtractingInvites}
                    onAddInvite={onAddInvite}
                    onRemoveInvite={remove}
                    onPasteInvites={onPasteInvites}
                    onBack={() => setScreen(TEAM_SETUP_SCREENS.STRUCTURE)}
                    onContinue={() => setScreen(TEAM_SETUP_SCREENS.CONFIRM)}
                />
            );
        case TEAM_SETUP_SCREENS.CONFIRM:
            return (
                <ConfirmTeamSetup
                    teams={selectedTeams}
                    roles={selectedRoles}
                    invites={invites}
                    isApprovedInitially={!!approval}
                    nextStepTitle={nextStepTitle}
                    onBack={() => setScreen(TEAM_SETUP_SCREENS.INVITES)}
                    onConfirm={onConfirm}
                />
            );
        default:
            return (
                <Segment basic style={{ padding: 0 }} loading={isGeneratingTeamStructure}>
                    <TeamStructure
                        teams={teams}
                        roles={availableRoles}
                        showRoleSuggestions={hasCustomRoles}
                        teamErrors={teamErrors}
                        teamLimit={teamLimit}
                        canContinue={canContinueToInvites}
                        isStale={isStale}
                        onRegenerate={onRegenerate}
                        onToggleTeam={(id) => updateTeam(id, { selected: !teams.find(team => team.id === id).selected })}
                        onRenameTeam={(id, name) => updateTeam(id, { name })}
                        onAddTeam={(name) => setTeams([...teams, { id: `custom_${Date.now()}`, name, purpose: '', selected: true, is_custom: true }])}
                        onRemoveTeam={(id) => setTeams(teams.filter(team => team.id !== id))}
                        onToggleRole={(id) => setRoles(roles.map(role => role.id === id && !role.locked ? { ...role, selected: !role.selected } : role))}
                        onBack={onBack}
                        onContinue={onContinueToInvites}
                    />
                </Segment>
            );
    }
}

export default TeamSetupStep;
